import React, { useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../../store/store";
import type { Repair } from "../../../../../store/Slice/repairSlice";
import { updateRepairAsync, fetchRepairsAsync } from "../../../../../store/Slice/repairSlice";
import { toast } from "react-toastify";

type Props = { repair: Repair; onClose: () => void };

const EditRepairModal: React.FC<Props> = ({ repair, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [form, setForm] = useState({
    companyName: repair.companyName || "",
    reseller: repair.reseller || "",
    serverId: repair.serverId || "",
    receivedBy: repair.receivedBy || "",
    departmentPerson: repair.departmentPerson || "",
    instrumentName: repair.instrumentName || "",
    handoverPerson: repair.handoverPerson || "",
    quantity: repair.quantity || 1,
    issueDescription: repair.issueDescription || "",
    remark: repair.remark || "",
    receivingDate: repair.receivingDate
      ? new Date(repair.receivingDate).toISOString().split("T")[0]
      : "",
    expectedDeliveryDate: repair.expectedDeliveryDate
      ? new Date(repair.expectedDeliveryDate).toISOString().split("T")[0]
      : "",
  });

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName.trim()) {
      toast.error("Company Name is required");
      return;
    }

    try {
      await dispatch(updateRepairAsync({ id: repair._id, data: form })).unwrap();
      dispatch(fetchRepairsAsync());
      toast.success("Repair updated successfully");
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Update failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div
        className="
        bg-white dark:bg-black
        w-full max-w-3xl 
        max-h-[90vh] overflow-hidden
        rounded-2xl shadow-2xl 
        border-2 border-black dark:border-white
        flex flex-col
        animate-in zoom-in-95 duration-300
      "
      >
        {/* Header */}
        <div className="relative bg-white dark:bg-white px-6 py-5 rounded-t-2xl border-b-2 border-black dark:border-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-black dark:text-black flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Repair Details
              </h2>
              <p className="text-black dark:text-gray-700 text-sm mt-1">Update repair information and tracking details</p>
            </div>
            <button
              onClick={onClose}
              className="text-black dark:text-black hover:bg-white/20 dark:hover:bg-black/20 rounded-lg p-2 transition-all duration-200 hover:rotate-90"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Order Number Badge */}
        <div className="px-6 pt-5 pb-3">
          <div className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-black dark:bg-white border-2 border-black dark:border-white shadow-sm">
            <div className="bg-white dark:bg-black rounded-lg p-2 border-2 border-black dark:border-white">
              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-300 dark:text-gray-700 uppercase tracking-wide">Order Number</p>
              <p className="text-lg font-bold text-white dark:text-black">{repair.orderNumber}</p>
            </div>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-6">
            {/* Company & Item Information */}
            <div className="bg-white dark:bg-black rounded-xl p-5 border-2 border-black dark:border-white shadow-sm">
              <h3 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Company & Item Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">
                    Company Name <span className="text-gray-600 dark:text-gray-400">*</span>
                  </label>
                  <input
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Instrument Name</label>
                  <input
                    name="instrumentName"
                    value={form.instrumentName}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter instrument name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Quantity</label>
                  <input
                    type="number"
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    min={1}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Reseller & ServerId Information */}
            <div className="bg-white dark:bg-black rounded-xl p-5 border-2 border-black dark:border-white shadow-sm">
              <h3 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Product Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Reseller</label>
                  <input
                    name="reseller"
                    value={form.reseller}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter reseller name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Server-ID</label>
                  <input
                    name="serverId"
                    value={form.serverId}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter server ID"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white dark:bg-black rounded-xl p-5 border-2 border-black dark:border-white shadow-sm">
              <h3 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Contact & Personnel
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Received By</label>
                  <input
                    name="receivedBy"
                    value={form.receivedBy}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter received by"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Handover Person</label>
                  <input
                    name="handoverPerson"
                    value={form.handoverPerson}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter handover person"
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Department Person</label>
                  <input
                    name="departmentPerson"
                    value={form.departmentPerson}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400"
                    placeholder="Enter department person"
                  />
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div className="bg-white dark:bg-black rounded-xl p-5 border-2 border-black dark:border-white shadow-sm">
              <h3 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                Dates
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">
                    Receiving Date
                  </label>
                  <input
                    type="date"
                    name="receivingDate"
                    value={form.receivingDate}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">
                    Expected Delivery Date
                  </label>
                  <input
                    type="date"
                    name="expectedDeliveryDate"
                    value={form.expectedDeliveryDate}
                    onChange={handleChange}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white"
                  />
                </div>
              </div>
            </div>


            {/* Issue & Remarks */}
            <div className="bg-white dark:bg-black rounded-xl p-5 border-2 border-black dark:border-white shadow-sm">
              <h3 className="text-sm font-semibold text-black dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Issue Details & Notes
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Issue Description</label>
                  <textarea
                    name="issueDescription"
                    value={form.issueDescription}
                    onChange={handleChange}
                    rows={4}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400 resize-none"
                    placeholder="Describe the issue in detail..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black dark:text-white mb-2">Remark</label>
                  <textarea
                    name="remark"
                    value={form.remark}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border-2 border-black dark:border-white bg-white dark:bg-black px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 transition-all duration-200 text-black dark:text-white placeholder-gray-400 resize-none"
                    placeholder="Additional remarks or notes..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-100 dark:bg-gray-900 border-t-2 border-black dark:border-white rounded-b-2xl">
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg bg-white dark:bg-black border-2 border-black dark:border-white text-black dark:text-white font-semibold hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-200 shadow-sm hover:shadow"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-8 py-2.5 rounded-lg bg-blue-600 dark:bg-white text-white dark:text-black  font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Update Repair
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRepairModal;